"use client";

import React, { useState } from "react";
import { Rocket, Loader2, Eye, EyeOff } from "lucide-react";
import { signUp } from "supertokens-auth-react/recipe/emailpassword";
import { useRouter } from "next/navigation";
import AuthBrandingPanel from "../components/auth-branding-panel";
import { translateAuthMessage } from "@workspace/core/authuser/auth-messages";
import { trackEvent } from "@/lib/analytics/client";

interface SignupPageProps {
  onBackClick: () => void;
  onLoginClick: () => void;
  redirectTo: string;
}

const SignupPage: React.FC<SignupPageProps> = ({
  onBackClick,
  onLoginClick,
  redirectTo,
}) => {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  type SignupResponse = {
    status: string;
    formFields?: Array<{ id: string; error?: string }>;
    reason?: string;
    message?: string;
  };

  const letterPattern = /[A-Za-z]/;
  const numberPattern = /\d/;
  const hasMinLength = password.trim().length >= 8;
  const hasLetter = letterPattern.test(password.trim());
  const hasNumber = numberPattern.test(password.trim());
  const passwordsMatch =
    Boolean(confirmPassword.trim()) &&
    confirmPassword.trim() === password.trim();

  const validatePasswords = () => {
    const sanitizedPassword = password.trim();
    const sanitizedConfirm = confirmPassword.trim();

    if (!sanitizedPassword || !sanitizedConfirm) {
      return "Senha e confirmação são obrigatórias.";
    }
    if (sanitizedPassword !== sanitizedConfirm) {
      return "As senhas devem ser iguais.";
    }
    if (sanitizedPassword.length < 8) {
      return "A senha deve conter pelo menos 8 caracteres.";
    }
    if (
      !letterPattern.test(sanitizedPassword) ||
      !numberPattern.test(sanitizedPassword)
    ) {
      return "A senha deve conter pelo menos uma letra e um número.";
    }

    return null;
  };

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const validationError = validatePasswords();
      if (validationError) {
        setError(validationError);
        return;
      }

      const response = await signUp({
        formFields: [
          {
            id: "email",
            value: email.trim().toLowerCase(),
          },
          {
            id: "password",
            value: password.trim(),
          },
        ],
      });

      const signupResponse = response as SignupResponse;

      if (signupResponse.status === "FIELD_ERROR") {
        signupResponse.formFields?.forEach((formField) => {
          if (formField.id === "email") {
            setError(formField.error ?? "E-mail inválido.");
          } else if (formField.id === "password") {
            setError(formField.error ?? "Senha inválida.");
          }
        });
      } else if (signupResponse.status === "EMAIL_ALREADY_EXISTS_ERROR") {
        setError("Este e-mail já existe na base antiga ou no cadastro atual.");
      } else if (signupResponse.status === "SIGN_UP_NOT_ALLOWED") {
        setError(signupResponse.reason ?? "Seu cadastro não está autorizado.");
      } else if (signupResponse.status === "GENERAL_ERROR") {
        setError(
          translateAuthMessage(
            signupResponse.message,
            "Não foi possível concluir o cadastro.",
          ),
        );
      } else if (signupResponse.status === "OK") {
        trackEvent("sign_up", { method: "password" });
        router.push(
          `/auth/verify-email?redirectTo=${encodeURIComponent(redirectTo)}`,
        );
      } else {
        setError("Não foi possível concluir o cadastro. Tente novamente.");
      }
    } catch (err) {
      setError(
        translateAuthMessage(
          err instanceof Error ? err.message : null,
          "Ocorreu um erro inesperado. Tente novamente.",
        ),
      );
      console.error("Signup failed:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch overflow-hidden font-display bg-background-light dark:bg-background-dark">
      <AuthBrandingPanel onLogoClick={onBackClick} />

      {/* Form Side */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center relative grid-pattern topo-lines px-6 overflow-y-auto py-20">
        <div className="w-full max-w-[440px] z-10">
          <div className="mb-10 text-center lg:text-left">
            <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
              Criar Conta
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              Inicie seu teste grátis de 14 dias hoje mesmo.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSignup}>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                E-mail Profissional
              </label>
              <input
                className="w-full bg-white dark:bg-[#1c2127] border border-slate-200 dark:border-slate-800 focus:border-primary dark:focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 rounded transition-all text-slate-900 dark:text-white placeholder:text-slate-400 outline-none"
                placeholder="seu@trabalho.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Senha
              </label>
              <div className="relative group">
                <input
                  className="w-full bg-white dark:bg-[#1c2127] border border-slate-200 dark:border-slate-800 focus:border-primary dark:focus:border-primary focus:ring-1 focus:ring-primary h-12 pl-4 pr-12 rounded transition-all text-slate-900 dark:text-white placeholder:text-slate-400 outline-none"
                  placeholder="Mínimo 8 caracteres"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <Eye className="size-5" />
                  ) : (
                    <EyeOff className="size-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Confirmar senha
              </label>
              <div className="relative group">
                <input
                  className="w-full bg-white dark:bg-[#1c2127] border border-slate-200 dark:border-slate-800 focus:border-primary dark:focus:border-primary focus:ring-1 focus:ring-primary h-12 pl-4 pr-12 rounded transition-all text-slate-900 dark:text-white placeholder:text-slate-400 outline-none"
                  placeholder="Repita sua senha"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                />
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={
                    showConfirmPassword ? "Ocultar senha" : "Mostrar senha"
                  }
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <Eye className="size-5" />
                  ) : (
                    <EyeOff className="size-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200/80 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40 p-4 space-y-1.5">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                Sua senha deve ter:
              </p>
              <p
                className={`text-xs ${hasMinLength ? "text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"}`}
              >
                Pelo menos 8 caracteres
              </p>
              <p
                className={`text-xs ${hasLetter ? "text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"}`}
              >
                Pelo menos uma letra (a-z ou A-Z)
              </p>
              <p
                className={`text-xs ${hasNumber ? "text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"}`}
              >
                Pelo menos um número (0-9)
              </p>
              <p
                className={`text-xs ${passwordsMatch ? "text-emerald-700 dark:text-emerald-400" : "text-slate-600 dark:text-slate-400"}`}
              >
                As senhas coincidem
              </p>
            </div>

            {error && (
              <div className="text-red-500 text-sm font-medium">{error}</div>
            )}

            <div className="pt-4">
              <button
                className="w-full cursor-pointer bg-primary hover:bg-primary/90 text-white font-bold h-14 rounded transition-all shadow-lg shadow-primary/20 flex items-center justify-center gap-2 group uppercase tracking-widest text-sm disabled:cursor-not-allowed disabled:opacity-70"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="size-5 animate-spin" />
                    <span>Criando Conta...</span>
                  </>
                ) : (
                  <>
                    <span>Criar Estação de Trabalho</span>
                    <Rocket className="size-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-8 text-center border-t border-slate-200 dark:border-slate-800 pt-8">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Já possui uma conta?{" "}
              <button
                onClick={onLoginClick}
                className="ml-1 cursor-pointer font-bold text-primary hover:text-primary/80 hover:underline"
              >
                Fazer Login
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
